import axios from 'axios';
import Cookies from 'js-cookie';
import jwt_decode from 'jwt-decode';
import urljoin from 'url-join';
import { AuthServiceEventV2 } from './AuthServiceProviderV2';
import { ResourceCaller } from './ResourceCaller';
import { AuthProxyUrlBuilder } from './AuthProxyUrlBuilder';
export class AuthServiceV2 {
    constructor(fag, sharedEventEmitter, authServiceOptions, authServiceV3, loginAllCallback, authServiceState, getConfig, serverRequest, logger) {
        this.fag = fag;
        this.sharedEventEmitter = sharedEventEmitter;
        this.authServiceOptions = authServiceOptions;
        this.authServiceV3 = authServiceV3;
        this.loginAllCallback = loginAllCallback;
        this.authServiceState = authServiceState;
        this.getConfig = getConfig;
        this.serverRequest = serverRequest;
        this.logger = logger;
        this.eventTimeout = 500;
        this.resourceCaller = new ResourceCaller(this.authServiceOptions);
        this.isAuthProxy = Boolean(authServiceOptions.isAuthProxy);
    }
    async isAuthenticated() {
        const isV3Authenticated = await this.authServiceV3.isAuthenticated();
        if (isV3Authenticated) {
            return true;
        }
        try {
            await this.getValidTokens();
            return true;
        }
        catch (e) {
            return false;
        }
    }
    async login(prompt, redirectUrl, options) {
        await this.sharedEventEmitter.emit(AuthServiceEventV2.SAVE_STATE);
        this.loginAllCallback(prompt, redirectUrl, options);
    }
    async logout(redirectUrl) {
        await this.sharedEventEmitter.emit(AuthServiceEventV2.LOGOUT);
        const csrfToken = Cookies.get('csrf_token');
        const logoutRedirectUrl = redirectUrl ||
            (typeof this.authServiceOptions.defaultLogoutRedirectUrl === 'function'
                ? this.authServiceOptions.defaultLogoutRedirectUrl()
                : this.authServiceOptions.defaultLogoutRedirectUrl);
        window.location.assign(AuthProxyUrlBuilder.getLogoutUrl(this.authServiceOptions, window.location.origin, csrfToken, logoutRedirectUrl));
    }
    async getAccessToken() {
        try {
            return await this.authServiceV3.getAccessToken();
        }
        catch (e) {
            const validTokens = await this.getValidTokens();
            return validTokens.access_token;
        }
    }
    async getIdToken() {
        try {
            return await this.authServiceV3.getIdToken();
        }
        catch (e) {
            const validTokens = await this.getValidTokens();
            return validTokens.id_token;
        }
    }
    subscribe(event, handlerFunction) {
        this.sharedEventEmitter.on(event, async () => {
            // eslint-disable-next-line no-promise-executor-return
            const timeoutPromise = new Promise((resolve) => setTimeout(resolve, this.eventTimeout));
            return Promise.race([timeoutPromise, handlerFunction()]);
        });
    }
    async get(resourceHostId, path, additionalHeaders) {
        return this.resourceCaller.call('GET', resourceHostId, path, undefined, additionalHeaders);
    }
    async put(resourceHostId, path, payload, additionalHeaders) {
        return this.resourceCaller.call('PUT', resourceHostId, path, payload, additionalHeaders);
    }
    async post(resourceHostId, path, payload, additionalHeaders) {
        return this.resourceCaller.call('POST', resourceHostId, path, payload, additionalHeaders);
    }
    async patch(resourceHostId, path, payload, additionalHeaders) {
        return this.resourceCaller.call('PATCH', resourceHostId, path, payload, additionalHeaders);
    }
    async delete(resourceHostId, path, payload, additionalHeaders) {
        return this.resourceCaller.call('DELETE', resourceHostId, path, payload, additionalHeaders);
    }
    getProxyUrl(resourceHostId, path) {
        var _a;
        const proxyUrl = this.resourceCaller.getProxyUrl(resourceHostId, path);
        if ((resourceHostId === null || resourceHostId === void 0 ? void 0 : resourceHostId.startsWith('form')) ||
            (this.isAuthProxy &&
                resourceHostId === this.authServiceOptions.authProxyFormMyaudiResourceHostId)) {
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.warn(`"${resourceHostId}" is reserved for retrieving the forms baseUrl with getForm${(resourceHostId === null || resourceHostId === void 0 ? void 0 : resourceHostId.startsWith('forms')) ? 's' : ''}BaseUrl`);
        }
        return proxyUrl;
    }
    async getFormDomainBaseUrl(path, formDomain = 'forms') {
        var _a, _b;
        const proxyUrl = this.isAuthProxy
            ? this.resourceCaller.getFormUrl()
            : this.resourceCaller.getProxyUrl();
        let baseUrlWithoutPath = proxyUrl;
        if (!this.isAuthProxy) {
            const key = formDomain.replace(/^([^/]+).*/, '$1');
            const config = this.isAuthProxy ? undefined : await this.getConfig(key);
            const baseUrl = (_a = config === null || config === void 0 ? void 0 : config.baseUrls[window.location.host]) !== null && _a !== void 0 ? _a : config === null || config === void 0 ? void 0 : config.baseUrls.default;
            if (baseUrl) {
                baseUrlWithoutPath = baseUrl.replace('{{proxyUrl}}', proxyUrl.replace(/\/$/, ''));
            }
            else {
                (_b = this.logger) === null || _b === void 0 ? void 0 : _b.warn(`"${formDomain}" base url is not available`);
                baseUrlWithoutPath = urljoin(proxyUrl, formDomain);
            }
        }
        return path
            ? urljoin(baseUrlWithoutPath, path.replace(/\/$/, ''))
            : baseUrlWithoutPath.replace(/\/$/, '');
    }
    async getFormsBaseUrl(path) {
        return this.getFormDomainBaseUrl(path, this.isAuthProxy && this.authServiceOptions.authProxyFormMyaudiResourceHostId
            ? this.authServiceOptions.authProxyFormMyaudiResourceHostId
            : 'forms');
    }
    async getFormBaseUrl(path) {
        return this.getFormDomainBaseUrl(path, this.isAuthProxy && this.authServiceOptions.authProxyFormMyaudiResourceHostId
            ? this.authServiceOptions.authProxyFormMyaudiResourceHostId
            : 'form/v1');
    }
    async getValidTokens() {
        const unauthMessage = 'User is not authenticated';
        if ((this.authServiceState.unauthResponseExpire || 0) >= Date.now()) {
            throw new Error(unauthMessage);
        }
        return this.authServiceState.mutex.use(async () => {
            if (!this.areTokensValid(this.authServiceState.tokens)) {
                const tokenResponse = await this.retrieveAndValidateTokens();
                if (tokenResponse) {
                    this.authServiceState.tokens = tokenResponse;
                    return tokenResponse;
                }
                // remember unauthenticated response for 10s
                this.authServiceState.unauthResponseExpire =
                    Date.now() + this.authServiceOptions.unauthResponseCacheTime;
                throw new Error(unauthMessage);
            }
            return this.authServiceState.tokens;
        });
    }
    getCookies(cookieKeys) {
        let cookie = '';
        cookieKeys.forEach((cookieKey) => {
            var _a;
            const cookieValue = (_a = this.serverRequest) === null || _a === void 0 ? void 0 : _a.cookies[cookieKey];
            if (cookieValue) {
                cookie += `${cookieKey}=${cookieValue};`;
            }
        });
        return cookie;
    }
    async retrieveAndValidateTokens() {
        const url = AuthProxyUrlBuilder.getInfoHookUrl(this.authServiceOptions);
        let tokenResponse;
        const sessionCookieKey = this.authServiceOptions.proxySessionCookieName;
        try {
            if (!this.serverRequest || !sessionCookieKey) {
                tokenResponse = await axios.get(url, {
                    withCredentials: true,
                    headers: this.isAuthProxy
                        ? {
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            'x-csrf-token': Cookies.get('csrf_token'),
                        }
                        : {},
                });
            }
            else {
                const shadowCookieKey = 'myaudi-shadow';
                tokenResponse = await axios.get(url, {
                    headers: {
                        Cookie: this.getCookies([sessionCookieKey, shadowCookieKey]),
                    },
                });
            }
        }
        catch (e) {
            if (axios.isAxiosError(e) && e.response && e.response.status === 401) {
                // Nothing is wrong, user is just not authenticated
                return undefined;
            }
            throw e;
        }
        // convert info json response from token proxy
        const tokens = {
            id_token: this.isAuthProxy
                ? tokenResponse.data.id_token
                : tokenResponse.headers['x-id-token'],
            access_token: tokenResponse.data.access_token,
        };
        if (this.areTokensValid(tokens)) {
            return tokens;
        }
        throw new Error('Token is not valid');
    }
    areTokensValid(tokens) {
        // TODO-MYA: the id token returned by myaudi token proxy is only 10 minutes valid. Check if it is a problem
        // to skip validation of id token as this is unclear if its ever used by the feature apps...??
        return Boolean(tokens && this.isJwtTokenValid(tokens.access_token));
    }
    // eslint-disable-next-line class-methods-use-this
    isJwtTokenValid(jwtToken) {
        try {
            const decoded = jwt_decode(jwtToken);
            const expireDate = decoded.exp * 1000;
            return expireDate > Date.now();
        }
        catch (e) {
            return false;
        }
    }
}
//# sourceMappingURL=AuthServiceV2.js.map